import { NOTIFICATION_TYPES } from "../../../../../../config/constants";
import {
  Button,
  Checkbox,
  Divider,
  Form,
  InputNumber, Popconfirm,
  Select,
  Steps,
} from "antd";
import { useEffect, useState } from "react";
import "../../MigrationRules.css";
import {
  PlusOutlined,
  CheckOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  CloseOutlined, QuestionCircleOutlined,
} from "@ant-design/icons";
import TablesService from "../../../../../../services/tables/TablesService";
import { GetTablesRequest } from "../../../../../../models/tables/GetTablesRequest";
import RuleValues from "../RuleValues";
import RuleConditions from "../RuleConditions";
import MappingService from "../../../../../../services/mappings/MappingService";
import { CreateMigrationRuleMapping } from "../../../../../../models/migrationrules/CreateMigrationRuleMapping";
import { useTranslation } from "react-i18next";
import NotificationsUtil from "../../../../../shared/utils/NotificationsUtil";

const { Step } = Steps;

const NewRuleOverview = ({
  entities,
  selectedEntity,
  selectedGroupCode, 
  isDirty,
  popConfirmOpen,
  setNewRuleOverviewDialogMode,
  setTriggerUpdate,
  setIsDirty,
  handlePopConfirmOpen,
}: {
  entities: any;
  newRuleOverviewDialogMode: boolean;
  setNewRuleOverviewDialogMode: (val: boolean) => void;
  selectedEntity: any;
  selectedGroupCode: any;
  isDirty: any;
  popConfirmOpen: any;
  setTriggerUpdate: any;
  setIsDirty: any;
  handlePopConfirmOpen: any;
}) => {
  const { t, i18n } = useTranslation("translations");
  const [current, setCurrent] = useState(0);

  const [fromTables, setFromTables] = useState<any>([]);
  const [selectedFromTable, setSelectedFromTable] = useState<any>(null);
  const [selectedRuleValueRows, setSelectedRuleValueRows] = useState<any>([]);
  const [newRuleValueModalEnabled, setNewRuleValueModalEnabled] =
    useState(false);
  const [newConditionModalEnabled, setNewConditionModalEnabled] =
    useState(false);
  const [selectedConditionRows, setSelectedConditionRows] = useState<any>([]);
  const [selectedMigrationStatus, setSelectedMigrationStatus] = useState<any>();
  const [weightInput, setWeightInput] = useState<any>();
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isNextBtnEnabled, setIsNextBtnEnabled] = useState(false);

  const next = () => {
    setCurrent(current + 1);
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const onCloseSteps = () => {
    setNewRuleOverviewDialogMode(false);
    setIsDirty(false);
  };

  const addMapping = () => {
    const model: CreateMigrationRuleMapping = {
      fromTableId: selectedFromTable,
      groupCode: selectedGroupCode,
      masterId: selectedRuleValueRows[0].id,
      conditionId: selectedConditionRows[0].id,
      weight: weightInput,
      migrationStatus: selectedMigrationStatus,
      isActive: isActive,
      environment: "",
    };
    MappingService.create(model)
      .then((response) => {
        onCloseSteps();
        setTriggerUpdate(true);
        NotificationsUtil.openNotificationDialog({
          description: t(
            "Page.Operation.MigrationRules.Tabs.RuleOverview.Notifications.MigrationRule.CreateSuccessDescription",
            {
              lngs: i18n.languages,
              defaultValue: "New migration rule created",
            }
          )!,
          type: NOTIFICATION_TYPES.SUCCESS,
          logError: false,
        });
      })
      .catch((error: any) => {
        NotificationsUtil.openNotificationDialog({
          description: error.response.data,
          type: NOTIFICATION_TYPES.ERROR,
          logError: true,
        });
      });
  };

  useEffect(() => {
    if (selectedEntity && selectedGroupCode) {
      const model: GetTablesRequest = {
        entityId: selectedEntity,
        operationId: 1,
        groupCode: selectedGroupCode,
      };

      TablesService.getTables(model).then((response) => {
        setFromTables(response.data);
      });
    }
  }, [selectedEntity, selectedGroupCode]);

  const steps = [
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.StepperSelectSource",
        {
          lngs: i18n.languages,
          defaultValue: "Select source",
        }
      ),
      content: t(
        "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.StepperSelectSourceDescription",
        {
          lngs: i18n.languages,
          defaultValue: "Data extraction source",
        }
      ),
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.StepperSelectRule",
        {
          lngs: i18n.languages,
          defaultValue: "Select a rule",
        }
      ),
      content: t(
        "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.StepperSelectRuleDescription",
        {
          lngs: i18n.languages,
          defaultValue: "Rule",
        }
      ),
      disabled: current === 0 && !selectedFromTable,
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.StepperSelectCondition",
        {
          lngs: i18n.languages,
          defaultValue: "Select a condition",
        }
      ),
      content: t(
        "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.StepperSelectConditionDescription",
        {
          lngs: i18n.languages,
          defaultValue: "Condition",
        }
      ),
      disabled: current <= 1 && selectedRuleValueRows.length === 0,
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.StepperMappingData",
        {
          lngs: i18n.languages,
          defaultValue: "Define mapping data",
        }
      ),
      content: t(
        "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.StepperMappingDataDescription",
        {
          lngs: i18n.languages,
          defaultValue: "Mapping data",
        }
      ),
      disabled: current <= 2 && selectedConditionRows.length === 0,
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.StepperOverview",
        {
          lngs: i18n.languages,
          defaultValue: "Overview",
        }
      ),
      content: t(
        "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.StepperOverviewDescription",
        {
          lngs: i18n.languages,
          defaultValue: "Overview",
        }
      ),
      disabled: current < 3,
    },
  ];

  const onFromTableChange = (value: string) => {
    setSelectedFromTable(value);
    if (!!value) {
      setIsDirty(true);
      next();
    } else {
      setIsDirty(false);
    }
  };
  const handleMigrationStatusChange = (value: string) => {
    setSelectedMigrationStatus(value);
  };
  const handleWeightChange = (value: number) => {
    setWeightInput(value);
  };

  const onStepChange = (value: number) => {
    setCurrent(value);
  };

  useEffect(() => {
    setIsNextBtnEnabled(
      (current === 0 && !!selectedFromTable) ||
        (current === 1 && !!selectedRuleValueRows.length) ||
        (current === 2 && !!selectedConditionRows.length) ||
        (current === 3 && !!weightInput && !!selectedMigrationStatus)
    );
  }, [
    current,
    selectedFromTable,
    selectedRuleValueRows,
    selectedConditionRows,
    weightInput,
    selectedMigrationStatus,
  ]);

  return (
    <div className="body-container" style={{height: '100%'}}>
      <div className="steps-container">
        <Steps
          direction="vertical"
          size="small"
          initial={0}
          current={current}
          onChange={onStepChange}
        >
          {steps.map((item) => (
            <Step
              key={item.title}
              title={item.title}
              description={item.content}
              disabled={item.disabled}
            />
          ))}
        </Steps>
      </div>
      <div className="overview-form-container">
        <div className="title-actions">
          <div>
            {current === 0 && (
              <span style={{ fontSize: "16px" }}>
                <b>
                  {t(
                    "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.AddNewMapping",
                    {
                      lngs: i18n.languages,
                      defaultValue: "Add new migration rule",
                    }
                  )}
                </b>
              </span>
            )}
            {current === 1 && (
              <Button
                onClick={() => setNewRuleValueModalEnabled(true)}
                type="primary"
                className="active-btn"
                size="small"
                icon={<PlusOutlined />}
                // disabled={current === 1}
              >
                {t(
                  "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.ActionNewRule",
                  {
                    lngs: i18n.languages,
                    defaultValue: "New rule",
                  }
                )}
              </Button>
            )}
            {current === 2 && (
              <Button
                onClick={() => setNewConditionModalEnabled(true)}
                type="primary"
                className="active-btn"
                size="small"
                icon={<PlusOutlined />}
                // disabled={current === 2}
              >
                {t(
                  "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.ActionNewCondition",
                  {
                    lngs: i18n.languages,
                    defaultValue: "New condition",
                  }
                )}
              </Button>
            )}
            {current === 4 && (
              <span style={{ fontSize: "16px" }}>
                <b>
                  {t(
                    "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.AddNewMapping",
                    {
                      lngs: i18n.languages,
                      defaultValue: "Add new migration rule",
                    }
                  )}
                </b>
              </span>
            )}
          </div>
          <div className="action-buttons">
            <Button
              onClick={() => prev()}
              type={current === 0 ? "default" : "ghost"}
              size="small"
              icon={<ArrowLeftOutlined />}
              disabled={current === 0}
            >
              {t("General.Labels.Back", {
                lngs: i18n.languages,
                defaultValue: "Back",
              })}
            </Button>
            {current === 4 && (
              <Button
                onClick={() => addMapping()}
                type="primary"
                className="active-btn"
                size="small"
                icon={<CheckOutlined />}
                // disabled={current < 4}
              >
                {t(
                  "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.AddMapping",
                  {
                    lngs: i18n.languages,
                    defaultValue: "Add mapping",
                  }
                )}
              </Button>
            )}
            {current < 4 && (
              <Button
                onClick={() => next()}
                disabled={!isNextBtnEnabled}
                type={!isNextBtnEnabled ? "default" : "ghost"}
                size="small"
                icon={<ArrowRightOutlined />}
              >
                {t("General.Labels.Next", {
                  lngs: i18n.languages,
                  defaultValue: "Next",
                })}
              </Button>
            )}

            {isDirty ? (
                <Popconfirm
                    style={{ width: "400px" }}
                    placement="topRight"
                    title={
                      <div
                          style={{
                            width: "400px",
                            wordBreak: "break-word",
                          }}
                      >
                        {t(
                            "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.ConfirmClose",
                            {
                              lngs: i18n.languages,
                              defaultValue:
                                  "Are you really sure that you want to close this drawer? All changes made will be lost.",
                            }
                        )}
                      </div>
                    }
                    icon={<QuestionCircleOutlined style={{ color: "red" }} />}
                    visible={popConfirmOpen}
                    onVisibleChange={handlePopConfirmOpen}
                    onConfirm={() => onCloseSteps()}
                    // onCancel={() => cancel}
                    okText={t("General.Actions.Yes", {
                      lngs: i18n.languages,
                      defaultValue: "Yes",
                    })}
                    cancelText={t("General.Actions.No", {
                      lngs: i18n.languages,
                      defaultValue: "No",
                    })}
                >
                  <Button
                      type="ghost"
                      icon={<CloseOutlined />}
                      size="small"
                  />
                </Popconfirm>
            ) : (
                <Button
                    type="ghost"
                    onClick={onCloseSteps}
                    icon={<CloseOutlined />}
                    size="small"
                />  
            )}
          </div>
        </div>
        <Divider />
        <div>
          {current === 0 && (
            <Form layout="vertical" wrapperCol={{ span: 8 }}>
              <Form.Item
                // className={styles.mb10px}
                label={t(
                  "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.FromTable",
                  {
                    lngs: i18n.languages,
                    defaultValue: "From table",
                  }
                )}
                tooltip={t(
                  "Page.Operation.MigrationRules.Tabs.RuleOverview.Tooltips.FromTable",
                  {
                    lngs: i18n.languages,
                    defaultValue: "",
                  }
                )}
                required
              >
                <Select
                  placeholder={t("General.Placeholders.SelectOption", {
                    lngs: i18n.languages,
                    defaultValue: "Select option",
                  })}
                  allowClear
                  disabled={current !== 0}
                  value={selectedFromTable}
                  options={fromTables.map((x: any) => ({
                    label: x.name,
                    value: x.id,
                  }))}
                  onChange={onFromTableChange}
                  onSelect={() => next()}
                ></Select>
              </Form.Item>
            </Form>
          )}
          <div className={current === 1 ? "" : "hide-step"}>
            <RuleValues
              entities={entities}
              selectedEntity={selectedEntity}
              newRuleValueModalEnabled={newRuleValueModalEnabled}
              setNewRuleValueModalEnabled={setNewRuleValueModalEnabled}
              selectable={true}
              multiSelect={false}
              selectedRows={selectedRuleValueRows}
              setSelectedRows={setSelectedRuleValueRows}
            />
          </div>
          <div className={current === 2 ? "" : "hide-step"}>
            <RuleConditions
              newConditionModalEnabled={newConditionModalEnabled}
              setNewConditionModalEnabled={setNewConditionModalEnabled}
              selectable={true}
              multiSelect={false}
              selectedRows={selectedConditionRows}
              setSelectedRows={setSelectedConditionRows}
            />
          </div>
          <Form
            className={current === 3 ? "" : "hide-step"}
            layout="vertical"
            wrapperCol={{ span: 8 }}
          >
            <Form.Item
              // className={styles.mb10px}
              label={t(
                "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.Weight",
                {
                  lngs: i18n.languages,
                  defaultValue: "Weight",
                }
              )}
              required
              tooltip={t(
                "Page.Operation.MigrationRules.Tabs.RuleOverview.Tooltips.CodeGroup",
                {
                  lngs: i18n.languages,
                  defaultValue: "",
                }
              )}
            >
              <InputNumber
                value={weightInput}
                onChange={handleWeightChange}
                min={1}
                placeholder={
                  t("General.Placeholders.EnterValue", {
                    lngs: i18n.languages,
                    defaultValue: "Enter value",
                  })!
                }
              />
            </Form.Item>
            <Form.Item
              // className={styles.mb10px}
              label={t(
                "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.MigrationStatus",
                {
                  lngs: i18n.languages,
                  defaultValue: "Migration status:",
                }
              )}
              required
              tooltip={t(
                "Page.Operation.MigrationRules.Tabs.RuleOverview.Tooltips.MigrationStatus",
                {
                  lngs: i18n.languages,
                  defaultValue: "",
                }
              )}
            >
              <Select
                placeholder={t("General.Placeholders.SelectOption", {
                  lngs: i18n.languages,
                  defaultValue: "Select option",
                })}
                allowClear
                disabled={current !== 3}
                options={[
                  {
                    label: t(
                      "Page.Operation.MigrationRules.Tabs.RuleOverview.Label.Migration",
                      {
                        lngs: i18n.languages,
                        defaultValue: "MIGRATION",
                      }
                    ),
                    value: "MIGRATION",
                  },
                  {
                    label: t(
                      "Page.Operation.MigrationRules.Tabs.RuleOverview.Label.NoMigration",
                      {
                        lngs: i18n.languages,
                        defaultValue: "MIGRATION",
                      }
                    ),
                    value: "NO MIGRATION",
                  },
                ]}
                value={selectedMigrationStatus}
                onChange={handleMigrationStatusChange}
              ></Select>
            </Form.Item>
            <Form.Item
              // className={styles.mb10px}
              required
              tooltip={t(
                "Page.Operation.MigrationRules.Tabs.RuleOverview.Tooltips.IsActive",
                {
                  lngs: i18n.languages,
                  defaultValue: "",
                }
              )}
            >
              <Checkbox
                checked={isActive}
                onClick={() => setIsActive(!isActive)}
              >
                {t(
                  "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.IsActive",
                  {
                    lngs: i18n.languages,
                    defaultValue: "Active Rule",
                  }
                )}
              </Checkbox>
            </Form.Item>
          </Form>
          {current === 4 && (
            <>
              <div className="overview-section">
                <span>
                  {t(
                    "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.SelectedSources",
                    {
                      lngs: i18n.languages,
                      defaultValue: "Selected sources",
                    }
                  )}
                </span>
                <table>
                  <tr>
                    <td>Group Code</td>
                    <td>{selectedGroupCode}</td>
                  </tr>
                  <tr>
                    <td>Entity</td>
                    <td>{entities.find((x:any) => x.id === selectedEntity)?.name}</td>
                  </tr>
                  <tr>
                    <td>From Table</td>
                    <td>
                      {
                        fromTables.find((x: any) => x.id === selectedFromTable)
                          .name
                      }
                    </td>
                  </tr>
                </table>
              </div>
              <div className="overview-section">
                <span>
                  {t(
                    "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.SelectedRule",
                    {
                      lngs: i18n.languages,
                      defaultValue: "Selected rule",
                    }
                  )}
                </span>
                <table>
                  <tr>
                    <td>Rule ID</td>
                    <td>
                      {selectedRuleValueRows?.length > 0
                        ? selectedRuleValueRows[0].id
                        : "Not selected"}
                    </td>
                  </tr>
                  <tr>
                    <td>Rule Name</td>
                    <td>
                      {selectedRuleValueRows?.length > 0
                        ? selectedRuleValueRows[0].ruleName
                        : "Not selected"}
                    </td>
                  </tr>
                  <tr>
                    <td>Rule Description</td>
                    <td>
                      {selectedRuleValueRows?.length > 0
                        ? selectedRuleValueRows[0].ruleDescription
                        : "Not selected"}
                    </td>
                  </tr>
                  <tr>
                    <td>Value</td>
                    <td>
                      {selectedRuleValueRows?.length > 0
                        ? selectedRuleValueRows[0].valueColumn
                        : "Not selected"}
                    </td>
                  </tr>
                  <tr>
                    <td>Join</td>
                    <td>
                      {selectedRuleValueRows?.length > 0
                        ? selectedRuleValueRows[0].joinCondition
                        : "Not selected"}
                    </td>
                  </tr>
                </table>
              </div>
              <div className="overview-section">
                <span>
                  {t(
                    "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.SelectedCondition",
                    {
                      lngs: i18n.languages,
                      defaultValue: "Selected condition",
                    }
                  )}
                </span>
                <table>
                  <tr>
                    <td>Condition</td>
                    <td>
                      {selectedConditionRows?.length > 0
                        ? selectedConditionRows[0].value
                        : "Not selected"}
                    </td>
                  </tr>
                </table>
              </div>
              <div className="overview-section">
                <span>
                  {t(
                    "Page.Operation.MigrationRules.Tabs.RuleOverview.Labels.MappingData",
                    {
                      lngs: i18n.languages,
                      defaultValue: "Mapping data",
                    }
                  )}
                </span>
                <table>
                  <tr>
                    <td>Weight</td>
                    <td>{weightInput}</td>
                  </tr>
                  <tr>
                    <td>Migration Status</td>
                    <td>{selectedMigrationStatus}</td>
                  </tr>
                  <tr>
                    <td>Active Rule</td>
                    <td>
                      {isActive ? 
                        t("General.Actions.Yes", {
                          lngs: i18n.languages,
                          defaultValue: "Yes",
                        }) : 
                        t("General.Actions.No", {
                          lngs: i18n.languages,
                          defaultValue: "No",
                        })
                      }
                    </td>
                  </tr>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewRuleOverview;
